import React from 'react';
import { Row, Col } from 'antd';
import PropTypes from 'prop-types';

import FastField from '../commons/FastField';
import AntSelect from '../commons/AntSelect';
import AntAutoComplete from '../commons/AntAutoComplete';
import AntDatePicker from '../commons/AntDatePicker';
import AntInput from '../commons/AntInput';
import AntTextArea from '../commons/AntTextArea';
import Info from '../commons/Info';

const GeneralForm = ({
  fields,
  directions,
  eventTypes,
  responsible,
  platform,
  game,
  versions,
  info,
  channels,
  displayConfig,
}) => {
  const getFieldProps = name => {
    const disabled = !fields.filter(item => item === name).length;
    return { name, disabled, 'data-test': name };
  };

  const renderDirection = () => (
    <Row gutter={40}>
      <Col span={6}>
        <FastField
          {...getFieldProps('direction')}
          label="Direction"
          placeholder="click and select..."
          component={AntSelect}
          options={directions}
        />
      </Col>
      <Col span={6} offset={12}>
        <Info text={info.responsible}>
          <FastField
            {...getFieldProps('responsible')}
            label="Responsible"
            placeholder="click and start typing..."
            component={AntSelect}
            options={responsible}
            showSearch
          />
        </Info>
      </Col>
    </Row>
  );
  const renderEventInfo = () => (
    <Row gutter={40}>
      <Col span={5}>
        <Info text={info.eventType}>
          <FastField
            {...getFieldProps('eventType')}
            label="Event type"
            placeholder="click and select..."
            component={AntSelect}
            options={eventTypes}
          />
        </Info>
      </Col>
      <Col span={5}>
        <FastField
          {...getFieldProps('game')}
          label="Games"
          placeholder="click and select..."
          component={AntSelect}
          options={game}
          mode={displayConfig.isMultiGames ? 'multiple' : undefined}
        />
      </Col>
      <Col span={5}>
        <FastField
          {...getFieldProps('platform')}
          label="Platforms"
          placeholder="click and select..."
          component={AntSelect}
          options={platform}
          mode="multiple"
        />
      </Col>
      <Col span={6} offset={3}>
        <Info text={info.dateTime}>
          <FastField
            {...getFieldProps('dateTime')}
            label="Event Date & Time"
            placeholder="click and select..."
            component={AntDatePicker}
          />
        </Info>
      </Col>
    </Row>
  );

  const renderAppVersion = () =>
    displayConfig.isAppVersion ? (
      <Col span={5}>
        <Info text={info.appVersion}>
          <FastField
            {...getFieldProps('appVersion')}
            label="App Version"
            placeholder="example 0.1.0"
            component={AntAutoComplete}
            options={versions}
          />
        </Info>
      </Col>
    ) : null;

  const renderAppVersionRange = () => (
    <>
      <Col span={5}>
        <Info text={info.minAppVersion}>
          <FastField
            {...getFieldProps('minAppVersion')}
            label="Min App Version"
            placeholder="example 0.1.0"
            component={AntAutoComplete}
            options={versions}
          />
        </Info>
      </Col>
      <Col span={5}>
        <Info text={info.maxAppVersion}>
          <FastField
            {...getFieldProps('maxAppVersion')}
            label="Max App Version"
            placeholder="example 0.1.0"
            component={AntAutoComplete}
            options={versions}
          />
        </Info>
      </Col>
    </>
  );

  const renderEventDetail = () => (
    <>
      <Row gutter={40}>
        {displayConfig.isMaxVersion
          ? renderAppVersionRange()
          : renderAppVersion()}
        {displayConfig.isServerVersion && (
          <Col span={5}>
            <FastField
              {...getFieldProps('serverVersion')}
              label="Server Version"
              placeholder="start typing..."
              component={AntAutoComplete}
              options={versions}
            />
          </Col>
        )}
        <Col span={8}>
          <Info text={info.link}>
            <FastField
              {...getFieldProps('notesLink')}
              label="Notes Link"
              placeholder="start typing..."
              component={AntInput}
            />
          </Info>
        </Col>
        {displayConfig.isEngineVersion && (
          <Col span={5}>
            <FastField
              {...getFieldProps('engineVersion')}
              label="Engine Version"
              placeholder="example 0.1.0"
              component={AntAutoComplete}
              options={versions}
            />
          </Col>
        )}
        {displayConfig.isСhannel && (
          <Col span={5}>
            <FastField
              {...getFieldProps('channel')}
              label="Channel"
              placeholder="click and select..."
              component={AntSelect}
              options={channels}
              mode="multiple"
            />
          </Col>
        )}
      </Row>
      <Row gutter={40}>
        <Col span={12}>
          <FastField
            {...getFieldProps('description')}
            label="Description"
            placeholder="start typing..."
            component={AntTextArea}
            style={{ height: 120 }}
          />
        </Col>
        {displayConfig.isAudience && (
          <Col span={12}>
            <Info text={info.audience}>
              <FastField
                {...getFieldProps('audience')}
                label="Audience"
                placeholder="start typing..."
                component={AntTextArea}
                style={{ height: 120 }}
              />
            </Info>
          </Col>
        )}
      </Row>
    </>
  );

  return (
    <>
      {renderDirection()}
      {renderEventInfo()}
      {renderEventDetail()}
    </>
  );
};

GeneralForm.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  directions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  eventTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  responsible: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  platform: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  game: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  channels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  displayConfig: PropTypes.shape({
    isAppVersion: PropTypes.bool,
    isMaxVersion: PropTypes.bool,
    isServerVersion: PropTypes.bool,
    isСhannel: PropTypes.bool,
    isAudience: PropTypes.bool,
    isMultiGames: PropTypes.bool,
    isEngineVersion: PropTypes.bool,
  }).isRequired,
  versions: PropTypes.arrayOf(PropTypes.string).isRequired,
  info: PropTypes.shape({
    responsible: PropTypes.string,
    eventType: PropTypes.string,
    dateTime: PropTypes.string,
    appVersion: PropTypes.string,
    minAppVersion: PropTypes.string,
    maxAppVersion: PropTypes.string,
    link: PropTypes.string,
    audience: PropTypes.string,
  }).isRequired,
};

export default GeneralForm;
